/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React from 'react';
import { CustomSlider, dateUtils } from '@opengeoweb/shared';
import { BackwardForwardStepButton } from '@opengeoweb/time-slider';
import { useSoundingsTranslation } from '../../utils/i18n';

export const TIMESLIDER_HEIGHT = 135;
const LABEL_MIN_WIDTH = 48;

const useMaxLabelCount = (ref: React.RefObject<HTMLDivElement>): number => {
  const [width, setWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    let resizeObserver: ResizeObserver;

    if (ref.current) {
      resizeObserver = new ResizeObserver((entry) => {
        const { width } = entry[0].target.getBoundingClientRect();
        setWidth(width);
      });

      resizeObserver.observe(ref.current);
    }

    return (): void => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [ref]);

  const maxLabelCount = Math.max(1, Math.floor(width / LABEL_MIN_WIDTH));
  return maxLabelCount;
};

const shouldHideLabel = (
  nItems: number,
  index: number,
  maxLabelCount: number,
): boolean => {
  const nthLabelsToShow = Math.ceil(nItems / maxLabelCount);

  return (
    nItems > maxLabelCount && // hide labels if too many items
    index !== nItems - 1 && // not last item (always shown)
    // only show every nth label, skip label within last n/2 items so end doesn't get cluttered
    (index % nthLabelsToShow !== 0 || nItems - 1 - index < nthLabelsToShow / 2)
  );
};

export interface SoundingsTimeSliderProps {
  timesteps: string[];
  selectedTime: string;
  updateSelectedTime: (date: string) => void;
}

export const SoundingsTimeSlider = ({
  timesteps,
  selectedTime,
  updateSelectedTime,
}: SoundingsTimeSliderProps): React.ReactElement => {
  const sliderRef = React.useRef<HTMLDivElement>(null);
  const maxLabelCount = useMaxLabelCount(sliderRef);

  const { i18n } = useSoundingsTranslation();
  const marks = timesteps
    .filter((date) => dateUtils.isValidIsoDateString(date))
    .map((date, index, arr) => ({
      value: dateUtils.unix(dateUtils.getUtcFromString(date)!),
      label: shouldHideLabel(arr.length, index, maxLabelCount)
        ? ''
        : dateUtils.dateToString(
            dateUtils.getUtcFromString(date),
            'iii d. HH:mm',
            true,
            i18n.language,
          ),
    }));

  const currentTimeIndex = React.useMemo(() => {
    if (timesteps.length === 0) {
      return -1;
    }

    const selectedUtc = dateUtils.getUtcFromString(selectedTime);
    if (!selectedUtc) {
      return 0;
    }
    const selectedUnix = dateUtils.unix(selectedUtc);

    const indexByExactTimestamp = timesteps.findIndex((time) => {
      const utcTime = dateUtils.getUtcFromString(time);
      if (!utcTime) {
        return false;
      }
      return dateUtils.unix(utcTime) === selectedUnix;
    });

    if (indexByExactTimestamp !== -1) {
      return indexByExactTimestamp;
    }
    const indexByExactString = timesteps.findIndex(
      (time) => time === selectedTime,
    );
    if (indexByExactString !== -1) {
      return indexByExactString;
    }

    // If selected time is not a sounding timestep, anchor on the nearest timestep
    // so forward/backward navigation continues from the expected point in time.
    return timesteps.reduce((nearestIndex, time, index) => {
      const utcTime = dateUtils.getUtcFromString(time);
      if (!utcTime) {
        return nearestIndex;
      }
      const currentDiff = Math.abs(dateUtils.unix(utcTime) - selectedUnix);
      const nearestUtc = dateUtils.getUtcFromString(timesteps[nearestIndex]);
      if (!nearestUtc) {
        return index;
      }
      const nearestDiff = Math.abs(dateUtils.unix(nearestUtc) - selectedUnix);
      return currentDiff < nearestDiff ? index : nearestIndex;
    }, 0);
  }, [selectedTime, timesteps]);

  const onStepBackward = React.useCallback(() => {
    const precedingIndex = Math.max(currentTimeIndex - 1, 0);
    updateSelectedTime(timesteps[precedingIndex]);
  }, [currentTimeIndex, updateSelectedTime, timesteps]);

  const onStepForward = React.useCallback(() => {
    const followingIndex = Math.min(currentTimeIndex + 1, timesteps.length - 1);
    updateSelectedTime(timesteps[followingIndex]);
  }, [currentTimeIndex, updateSelectedTime, timesteps]);

  return (
    <div
      ref={sliderRef}
      style={{
        display: 'flex',
        paddingTop: '32px',
        marginRight: '80px',
        marginLeft: '24px',
        height: `${TIMESLIDER_HEIGHT}px`,
      }}
    >
      <div
        style={{
          minWidth: '60px',
          marginTop: '2px',
          display: 'flex',
          gap: '4px',
        }}
      >
        <BackwardForwardStepButton
          isDisabled={marks.length === 0}
          isForwardStep={false}
          onClickBFButton={onStepBackward}
        />
        <BackwardForwardStepButton
          isDisabled={marks.length === 0}
          isForwardStep={true}
          onClickBFButton={onStepForward}
        />
      </div>
      {marks.length === 0 ? (
        <CustomSlider
          disabled
          max={1}
          min={0}
          orientation="horizontal"
          value={0.5}
        />
      ) : (
        <CustomSlider
          extraStyles={{
            margin: '0px 20px 0px 20px',
            '.MuiSlider-markLabel': {
              transform: 'translate(-90%, 20px) rotate(-45deg)',
              transformOrigin: 'top center',
            },
          }}
          marks={marks}
          max={marks[marks.length - 1].value}
          min={marks[0].value}
          onChange={(_, value) => {
            updateSelectedTime(
              dateUtils.fromUnix(value as number).toISOString(),
            );
          }}
          orientation="horizontal"
          slotProps={{
            // Workaround for MUI bug where labels are just draggable area and clicking on one
            // does not necessarily select the described timestep
            markLabel: {
              onPointerDown: (event) => {
                event.preventDefault();
                const markIndex =
                  event.currentTarget.getAttribute('data-index');
                const mark = marks[Number(markIndex)];
                const { value } = mark;
                updateSelectedTime(
                  dateUtils.fromUnix(value as number).toISOString(),
                );
              },
            },
          }}
          step={null}
          value={dateUtils.unix(dateUtils.getUtcFromString(selectedTime)!)}
          valueLabelDisplay="on"
          valueLabelFormat={(value) =>
            dateUtils.dateToString(
              dateUtils.fromUnix(value),
              "yyyy iii dd MMM HH:mm 'UTC'",
              true,
              i18n.language,
            )
          }
        />
      )}
    </div>
  );
};
